import type { CSSProperties, JSX } from 'react';
import type { Rank } from '@tira/engines/gera/pieces';
import './geraArt.css';

/**
 * The fifteen insignia, one per rank (design 1f).
 *
 * The board deliberately shows none of these — a standee on the desk is velvet
 * and brass and nothing else, because the rank is the whole game. They belong
 * to the two places where a piece is yours to look at: the setup tray you
 * deploy from, and the ladder you read to learn what beats what.
 *
 * Drawn rather than lettered. "Opisyal 7" is a database row; an eagle is a
 * colonel, and a player who has seen it once knows the piece across the desk
 * for the rest of their life. Every measurement here is the spec's own, laid
 * out in a 40×30 box and scaled by `--gins-k` so the tray and the ladder can
 * take the same art at different sizes.
 *
 * Generic real-world grades only — the venue's IP guardrail (PRD §5/O2) rules
 * out anybody's proprietary set, and ordinary military insignia are exactly
 * what §9 G6 asks for.
 */

const GOLD = 'linear-gradient(180deg,#F0D488,#C9A227 55%,#8F6E14)';
const SILVER = 'linear-gradient(180deg,#EBEEF4,#B9C0CC 55%,#7C8594)';

/** Stars, and how tight they sit — five at 12px, one at 17px (spec 1f). */
const STARS: Record<string, { glyph: string; size: number; track: number }> = {
  'heneral-5': { glyph: '★★★★★', size: 12, track: 0 },
  'heneral-4': { glyph: '★★★★', size: 13, track: 1 },
  'heneral-3': { glyph: '★★★', size: 14, track: 1.5 },
  'heneral-2': { glyph: '★★', size: 15, track: 2 },
  'heneral-1': { glyph: '★', size: 17, track: 0 },
};

export function Insignia({ rank }: { rank: Rank }): JSX.Element {
  return (
    <span className="gins" aria-hidden="true">
      {mark(rank)}
    </span>
  );
}

function mark(rank: Rank): JSX.Element {
  const star = STARS[rank];
  if (star) {
    return (
      <span
        className="gins__stars"
        style={{ fontSize: `${star.size}px`, letterSpacing: `${star.track}px` }}
      >
        {star.glyph}
      </span>
    );
  }

  switch (rank) {
    // Colonel — the eagle. One shape, clipped, because a bird drawn in boxes
    // at this size reads as a bird and an SVG here would be the only one.
    case 'opisyal-7':
      return (
        <span className="gins__eagle">
          <span className="gins__eagle-head" />
          <span className="gins__eagle-beak" />
          <span className="gins__eagle-wings" />
        </span>
      );
    // Lt. Colonel — silver oak leaf. Major — the same leaf in gold, which is
    // the real-world distinction and the only thing separating the two.
    case 'opisyal-6':
      return <Leaf fill="#C9D0DB" vein="#7E8896" />;
    case 'opisyal-5':
      return <Leaf fill="#E2BE4C" vein="#A2811B" />;
    // Captain, two gold bars. 1st Lt one silver, 2nd Lt one gold.
    case 'opisyal-4':
      return <Bars metal={GOLD} count={2} />;
    case 'opisyal-3':
      return <Bars metal={SILVER} count={1} />;
    case 'opisyal-2':
      return <Bars metal={GOLD} count={1} />;
    // Sergeant three chevrons, private one — the line the whole army is made of.
    case 'opisyal-1':
      return <Chevrons count={3} />;
    case 'tanod':
      return <Chevrons count={1} />;
    // The spy: crossed blades, the one piece that beats everything above it.
    case 'tiktik':
      return (
        <span className="gins__swords">
          <span className="gins__blade" data-lean="a" />
          <span className="gins__blade" data-lean="b" />
          <span className="gins__guard" data-lean="a" />
          <span className="gins__guard" data-lean="b" />
        </span>
      );
    // The flag. Loses to everything, and is the entire point of the match.
    case 'watawat':
      return (
        <span className="gins__flag">
          <span className="gins__pole" />
          <span className="gins__banner" />
        </span>
      );
    default:
      return <span className="gins__stars">★</span>;
  }
}

function Leaf({ fill, vein }: { fill: string; vein: string }): JSX.Element {
  return (
    <span className="gins__leaf" style={{ '--leaf': fill, '--vein': vein } as CSSProperties}>
      <span className="gins__leaf-blob" />
      <span className="gins__leaf-vein" />
      <span className="gins__leaf-stem" />
    </span>
  );
}

function Bars({ metal, count }: { metal: string; count: number }): JSX.Element {
  return (
    <span className="gins__bars">
      {Array.from({ length: count }, (_, i) => (
        <span className="gins__bar" key={i} style={{ background: metal }} />
      ))}
    </span>
  );
}

function Chevrons({ count }: { count: number }): JSX.Element {
  return (
    <span className="gins__chevs">
      {Array.from({ length: count }, (_, i) => (
        <span className="gins__chev" key={i} />
      ))}
    </span>
  );
}
